// @ts-check
// Configurações > Notificações (contrato 08, seção 7): por tipo de aviso, receber no sino e por e-mail; para
// administradores, os prazos da escola (tarefa vencendo e mensagem sem resposta).
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { PageCard, PageHeader } from '../page/page-card.js';
import { SettingsSection } from '../settings/settings-pages.js';
import { hasCurrentPermissionFlag } from '../../data/metadata-store.js';
import { getNotificationPreferences, updateNotificationPreferences } from '../../data/automations.js';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { Skeleton, Toggle } from '../../ui/feedback.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { NOTIFICATION_TYPE_ICONS } from './notification-bell.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/automations.js').NotificationPreference} NotificationPreference
 * @typedef {import('../../data/automations.js').NotificationSettings} NotificationSettings
 */

export function SettingsNotificationsPage() {
  // prazos dos avisos são configuração da instituição (flag WORKSPACE)
  const isAdmin = hasCurrentPermissionFlag('WORKSPACE');
  const [preferences, setPreferences] = useState(/** @type {NotificationPreference[] | null} */ (null));
  const [settings, setSettings] = useState(/** @type {NotificationSettings | null} */ (null));
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  useEffect(() => {
    document.title = `${t('notifications.settingsTitle')} - ${t('navigation.settings')}`;
    getNotificationPreferences()
      .then((data) => {
        setPreferences(data.preferences);
        setSettings(data.settings);
      })
      .catch((error) => {
        showErrorSnackbar(error);
        setPreferences([]);
      });
  }, []);

  /** @param {NotificationPreference} preference @param {Partial<NotificationPreference>} patch */
  const changePreference = async (preference, patch) => {
    const next = { ...preference, ...patch };
    setPreferences((current) => current?.map((item) => (item.type === preference.type ? next : item)) ?? null);
    try {
      const data = await updateNotificationPreferences({ preferences: [next] });
      setPreferences(data.preferences);
    } catch (error) {
      setPreferences((current) => current?.map((item) => (item.type === preference.type ? preference : item)) ?? null);
      showErrorSnackbar(error);
    }
  };

  const saveSettings = async (/** @type {Event} */ event) => {
    event.preventDefault();
    if (!settings) return;
    setIsSavingSettings(true);
    try {
      const data = await updateNotificationPreferences({ settings });
      setSettings(data.settings);
      showSuccessSnackbar(t('notifications.settingsSaved'));
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsSavingSettings(false);
    }
  };

  return html`<${PageCard} header=${html`<${PageHeader} icon="IconBell" title=${t('notifications.settingsTitle')} />`}>
    <div class="settings-page">
      <${SettingsSection} title=${t('notifications.preferencesTitle')} description=${t('notifications.preferencesDescription')}>
        ${preferences === null
          ? html`<${Skeleton} height=${200} radius=${8} />`
          : html`<div class="settings-table notification-preferences">
              <div class="settings-table-row settings-table-header" data-columns="notifications">
                <span>${t('notifications.columns.type')}</span>
                <span class="notification-preferences-center">${t('notifications.columns.inApp')}</span>
                <span class="notification-preferences-center">${t('notifications.columns.email')}</span>
              </div>
              ${preferences.map((preference) => html`<div class="settings-table-row" data-columns="notifications" key=${preference.type}>
                <span class="notification-preferences-type">
                  <${Icon} name=${NOTIFICATION_TYPE_ICONS[/** @type {keyof typeof NOTIFICATION_TYPE_ICONS} */ (preference.type)] ?? 'IconBell'} size=${16} />
                  <span>
                    <span class="settings-table-name">${t(`notifications.typeLabels.${preference.type}`)}</span>
                    <span class="settings-table-muted notification-preferences-description">${t(`notifications.typeDescriptions.${preference.type}`)}</span>
                  </span>
                </span>
                <span class="notification-preferences-center">
                  <${Toggle} value=${preference.inAppEnabled} ariaLabel=${t('notifications.toggleInApp', { type: t(`notifications.typeLabels.${preference.type}`) })}
                    onChange=${(value) => changePreference(preference, { inAppEnabled: value })} />
                </span>
                <span class="notification-preferences-center">
                  <${Toggle} value=${preference.emailEnabled && preference.inAppEnabled} ariaLabel=${t('notifications.toggleEmail', { type: t(`notifications.typeLabels.${preference.type}`) })}
                    onChange=${preference.inAppEnabled ? (value) => changePreference(preference, { emailEnabled: value }) : undefined} />
                </span>
              </div>`)}
            </div>`}
      <//>
      ${settings
        ? html`<${SettingsSection} title=${t('notifications.deadlinesTitle')} description=${t('notifications.deadlinesDescription')}>
            <form class="notification-deadlines" onSubmit=${saveSettings}>
              <div class="form-field-row">
                <label class="form-field">
                  <span class="form-field-label">${t('notifications.taskDueSoonHours')}</span>
                  <input class="text-input" type="number" min="1" max="720" value=${settings.taskDueSoonHours} disabled=${!isAdmin}
                    onInput=${(event) => setSettings({ ...settings, taskDueSoonHours: Number(event.currentTarget.value) })} />
                </label>
                <label class="form-field">
                  <span class="form-field-label">${t('notifications.unansweredMessageHours')}</span>
                  <input class="text-input" type="number" min="1" max="720" value=${settings.unansweredMessageHours} disabled=${!isAdmin}
                    onInput=${(event) => setSettings({ ...settings, unansweredMessageHours: Number(event.currentTarget.value) })} />
                </label>
              </div>
              ${isAdmin
                ? html`<div><${Button} title=${t('common.save')} icon="IconDeviceFloppy" accent="blue" size="small" type="submit" isLoading=${isSavingSettings} /></div>`
                : html`<p class="channel-card-note"><${Icon} name="IconLock" size=${14} />${t('notifications.adminOnly')}</p>`}
            </form>
          <//>`
        : null}
    </div>
  <//>`;
}
